import { useState, useMemo } from 'react';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';

type UseSitrepSectionSelectorReturn = {
  /**
   * The active section index, safe for direct use:
   *
   * - Defaults to 0 when no tab has been selected yet
   * - Clamps to the last index when sections are removed
   * - Null only when there are no sections
   */
  activeSectionIndex: number | null;
  setActiveSectionIndex: (index: number | null) => void;
  /** The currently active section, or null when nothing is selected. */
  activeSection: SitrepSectionType | null;
};

/**
 * Manages section-tab selection state for a sitrep card.
 *
 * `activeSectionIndex` is derived from raw state and is always safe to use
 * directly — it handles default selection, clamping, and empty states.
 */
const useSitrepSectionSelector = (
  sections: SitrepSectionType[] | undefined
): UseSitrepSectionSelectorReturn => {
  const [rawIndex, setActiveSectionIndex] = useState<number | null>(null);

  const activeSectionIndex = useMemo((): number | null => {
    if (!sections?.length) return null;
    if (rawIndex === null) return 0;
    if (rawIndex >= sections.length) return sections.length - 1;
    return rawIndex;
  }, [sections, rawIndex]);

  const activeSection = useMemo((): SitrepSectionType | null => {
    if (activeSectionIndex === null || !sections) return null;
    return sections[activeSectionIndex] ?? null;
  }, [sections, activeSectionIndex]);

  return {
    activeSectionIndex,
    setActiveSectionIndex,
    activeSection,
  };
};

export default useSitrepSectionSelector;
